import { useState, useEffect } from "react";
import { 
  Users, Building2, Link2, Unlink, Eye, EyeOff, Search, 
  ChevronDown, ChevronRight, BarChart3, Package, Wrench,
  AlertCircle, CheckCircle2, XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRicambisti() {
  const [ricambisti, setRicambisti] = useState([]);
  const [officine, setOfficine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRicambista, setSelectedRicambista] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [visiblePassword, setVisiblePassword] = useState("");
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [ricambistaStats, setRicambistaStats] = useState(null);
  const [expandedRicambista, setExpandedRicambista] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ricRes, offRes] = await Promise.all([
        authFetch(`${API}/admin/ricambisti`, { }),
        authFetch(`${API}/admin/officine`, { })
      ]);
      
      if (ricRes.ok) setRicambisti(await ricRes.json());
      if (offRes.ok) setOfficine(await offRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkOfficina = async (officinaId) => {
    if (!selectedRicambista) return;
    
    try {
      const response = await authFetch(`${API}/admin/link-officina-ricambista`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          officina_id: officinaId,
          ricambista_id: selectedRicambista.user_id
        })
      });
      
      if (response.ok) {
        toast.success("Officina collegata con successo");
        fetchData();
        setShowLinkDialog(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel collegamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleUnlinkOfficina = async (officinaId, ricambistaId) => {
    try {
      const response = await authFetch(`${API}/admin/unlink-officina-ricambista`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          officina_id: officinaId,
          ricambista_id: ricambistaId
        })
      });
      
      if (response.ok) {
        toast.success("Collegamento rimosso");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleViewPassword = async (user) => {
    setPasswordUser(user);
    setShowPasswordDialog(true);
    setVisiblePassword("");
  };

  const confirmViewPassword = async () => {
    if (!passwordUser) return;
    
    try {
      const response = await authFetch(`${API}/admin/users/${passwordUser.user_id}/password`, {
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisiblePassword(data.password);
      } else {
        toast.error("Password non disponibile");
      }
    } catch (error) {
      toast.error("Errore nel recupero password");
    }
  };

  const handleViewStats = async (ricambista) => {
    try {
      const response = await authFetch(`${API}/admin/ricambisti/${ricambista.user_id}/stats`, {
      });
      
      if (response.ok) {
        setRicambistaStats(await response.json());
        setShowStatsDialog(true);
      }
    } catch (error) {
      toast.error("Errore nel caricamento statistiche");
    }
  };

  const getLinkedOfficine = (ricambistaId) => {
    return officine.filter(o => o.linked_ricambista_id === ricambistaId);
  };

  const getUnlinkedOfficine = () => {
    return officine.filter(o => !o.linked_ricambista_id);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "ATTIVO":
        return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400">Attivo</span>;
      case "BLOCCATO":
        return <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400">Bloccato</span>;
      case "SOSPESO":
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400">Sospeso</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-zinc-500/20 text-zinc-400">{status}</span>;
    }
  };

  const filteredRicambisti = ricambisti.filter(r => 
    r.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Gestione Ricambisti e Officine</h1>
          <p className="text-zinc-400 text-sm mt-1">
            Collega le officine ai ricambisti e gestisci gli accessi
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Package className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{ricambisti.length}</p>
              <p className="text-xs text-zinc-400">Ricambisti</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <Wrench className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{officine.length}</p>
              <p className="text-xs text-zinc-400">Officine Totali</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/20">
              <Link2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {officine.filter(o => o.linked_ricambista_id).length}
              </p>
              <p className="text-xs text-zinc-400">Officine Collegate</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/20">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {getUnlinkedOfficine().length}
              </p>
              <p className="text-xs text-zinc-400">Officine da Collegare</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Cerca ricambista..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Ricambisti List */}
      <div className="space-y-4">
        {filteredRicambisti.map(ricambista => {
          const linkedOfficine = getLinkedOfficine(ricambista.user_id);
          const isExpanded = expandedRicambista === ricambista.user_id;
          
          return (
            <div key={ricambista.user_id} className="card">
              {/* Ricambista Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50"
                onClick={() => setExpandedRicambista(isExpanded ? null : ricambista.user_id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Package className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{ricambista.company_name}</h3>
                      {getStatusBadge(ricambista.status)}
                    </div>
                    <p className="text-sm text-zinc-400">
                      {ricambista.name} • @{ricambista.username} • {ricambista.city}, {ricambista.province}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-primary">{linkedOfficine.length}</p>
                    <p className="text-xs text-zinc-400">Officine collegate</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPassword(ricambista);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewStats(ricambista);
                      }}
                    >
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRicambista(ricambista);
                        setShowLinkDialog(true);
                      }}
                    >
                      <Link2 className="w-4 h-4 mr-1" />
                      Collega
                    </Button>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-zinc-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-zinc-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Linked Officine */}
              {isExpanded && (
                <div className="border-t border-zinc-800 p-4">
                  <h4 className="text-sm font-semibold text-zinc-400 mb-3">
                    Officine Collegate ({linkedOfficine.length})
                  </h4>
                  {linkedOfficine.length === 0 ? (
                    <p className="text-sm text-zinc-500 italic">Nessuna officina collegata</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {linkedOfficine.map(officina => (
                        <div 
                          key={officina.user_id}
                          className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-white">{officina.company_name}</p>
                              <p className="text-xs text-zinc-400">
                                {officina.name} • {officina.city}
                              </p>
                              <div className="mt-1">
                                {getStatusBadge(officina.status)}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewPassword(officina)}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleUnlinkOfficina(officina.user_id, ricambista.user_id)}
                              >
                                <Unlink className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unlinked Officine Section */}
      {getUnlinkedOfficine().length > 0 && (
        <div className="card p-4">
          <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Officine da Collegare ({getUnlinkedOfficine().length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {getUnlinkedOfficine().map(officina => (
              <div 
                key={officina.user_id}
                className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
              >
                <p className="font-medium text-white">{officina.company_name}</p>
                <p className="text-xs text-zinc-400">{officina.city}, {officina.province}</p>
                <div className="mt-2 flex items-center justify-between">
                  {getStatusBadge(officina.status)}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewPassword(officina)}
                  >
                    <Eye className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Collega Officina a {selectedRicambista?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-zinc-400">
              Seleziona un'officina da collegare a questo ricambista
            </p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {getUnlinkedOfficine().map(officina => (
                <div 
                  key={officina.user_id}
                  className="p-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 cursor-pointer flex items-center justify-between"
                  onClick={() => handleLinkOfficina(officina.user_id)}
                >
                  <div>
                    <p className="font-medium text-white">{officina.company_name}</p>
                    <p className="text-xs text-zinc-400">
                      {officina.name} • {officina.city}, {officina.province}
                    </p>
                  </div>
                  {getStatusBadge(officina.status)}
                </div>
              ))}
              {getUnlinkedOfficine().length === 0 && (
                <p className="text-center text-zinc-500 py-8">
                  Tutte le officine sono già collegate
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={(open) => {
        setShowPasswordDialog(open);
        if (!open) {
          setVisiblePassword("");
          setPasswordUser(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Visualizza Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {passwordUser && (
              <div className="p-4 rounded-lg bg-zinc-800">
                <p className="text-sm text-zinc-400">Utente</p>
                <p className="font-medium text-white">{passwordUser.company_name}</p>
                <p className="text-xs text-zinc-500">@{passwordUser.username}</p>
              </div>
            )}
            
            {!visiblePassword ? (
              <div className="text-center py-4">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <p className="text-sm text-zinc-400 mb-4">
                  La visualizzazione della password verrà registrata nel log di sistema.
                </p>
                <Button onClick={confirmViewPassword}>
                  Conferma e Visualizza Password
                </Button>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-sm text-zinc-400 mb-2">Password</p>
                <div className="flex items-center gap-2">
                  <code className="text-lg font-mono text-green-400">{visiblePassword}</code>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Dialog */}
      <Dialog open={showStatsDialog} onOpenChange={setShowStatsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Statistiche {ricambistaStats?.ricambista?.company_name}
            </DialogTitle>
          </DialogHeader>
          {ricambistaStats && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-zinc-800 text-center">
                  <p className="text-2xl font-bold text-primary">
                    {ricambistaStats.stats.total_officine}
                  </p>
                  <p className="text-xs text-zinc-400">Officine</p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800 text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {ricambistaStats.stats.total_orders}
                  </p>
                  <p className="text-xs text-zinc-400">Ordini Totali</p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800 text-center">
                  <p className="text-2xl font-bold text-blue-400">
                    €{ricambistaStats.stats.total_revenue?.toFixed(2) || "0.00"}
                  </p>
                  <p className="text-xs text-zinc-400">Fatturato</p>
                </div>
                <div className="p-3 rounded-lg bg-zinc-800 text-center">
                  <p className="text-2xl font-bold text-yellow-400">
                    {ricambistaStats.stats.avg_orders_per_officina?.toFixed(1) || "0"}
                  </p>
                  <p className="text-xs text-zinc-400">Media Ordini/Officina</p>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-zinc-400 mb-2">Officine Collegate</h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {ricambistaStats.linked_officine?.map(off => (
                    <div key={off.user_id} className="p-2 rounded bg-zinc-800/50 flex justify-between">
                      <span className="text-white">{off.company_name}</span>
                      <span className="text-zinc-400 text-sm">{off.city}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
